'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cn } from '../utils.ts';

interface CheckboxProps extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: ReactNode;
  hint?: ReactNode;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { label, hint, id, className, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `checkbox-${reactId}`;
  return (
    <div className="flex items-start gap-3">
      <CheckboxPrimitive.Root
        ref={ref}
        id={inputId}
        className={cn(
          'mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-sm border border-border-subtle bg-surface',
          'transition-colors duration-fast ease-clickt',
          'hover:border-midnight/40',
          'focus-visible:outline-none focus-visible:shadow-focus-ring',
          'data-[state=checked]:bg-electric data-[state=checked]:border-electric',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      >
        <CheckboxPrimitive.Indicator>
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label ? (
        <label htmlFor={inputId} className="text-body-md text-midnight leading-snug cursor-pointer">
          {label}
          {hint ? <span className="block text-[13px] text-cool-gray mt-0.5">{hint}</span> : null}
        </label>
      ) : null}
    </div>
  );
});
